<template>
  <a-modal
    :open="visible"
    :title="modalTitle"
    width="80%"
    :footer="null"
    class="file-detail-modal"
    @cancel="handleClose"
  >
    <div v-if="fileDetail" class="file-detail-content">
      <!-- 文件基本信息 -->
      <div class="mb-6 border rounded-lg p-4" :class="getOverallCoverageClass()">
        <div class="mb-3 flex items-center justify-between">
          <h4 class="text-lg font-semibold text-gray-800">文件概览</h4>
          <span class="text-2xl font-bold" :class="getCoverageTextClass()">{{ coveragePercentage }}%</span>
        </div>
        <p class="mb-3 text-sm font-mono text-gray-600">{{ fileDetail.file_path }}</p>
        <div class="h-3 w-full rounded-full bg-gray-200">
          <div
            class="h-3 rounded-full transition-all duration-1000"
            :class="getCoverageBarClass()"
            :style="{ width: `${coveragePercentage}%` }"
          ></div>
        </div>
      </div>

      <!-- 函数覆盖详情 -->
      <div class="grid grid-cols-1 mb-6 gap-6 md:grid-cols-2">
        <!-- 已覆盖函数 -->
        <div class="border border-green-200 rounded-lg bg-green-50 p-4">
          <div class="mb-3 flex items-center">
            <check-circle-outlined class="mr-2 text-green-600" />
            <h5 class="font-semibold text-green-800">已覆盖函数 ({{ coveredFunctionsCount }})</h5>
          </div>
          <div class="max-h-48 overflow-y-auto space-y-2">
            <div
              v-for="func in fileDetail.covered_functions"
              :key="func"
              class="flex items-center border border-green-100 rounded bg-white p-2"
            >
              <function-outlined class="mr-2 text-green-500" />
              <code class="text-sm text-gray-800">{{ func }}</code>
            </div>
            <p v-if="coveredFunctionsCount === 0" class="text-sm text-green-600">暂无已覆盖函数</p>
          </div>
        </div>

        <!-- 未覆盖函数 -->
        <div class="border border-red-200 rounded-lg bg-red-50 p-4">
          <div class="mb-3 flex items-center">
            <exclamation-circle-outlined class="mr-2 text-red-600" />
            <h5 class="font-semibold text-red-800">未覆盖函数 ({{ uncoveredFunctionsCount }})</h5>
          </div>
          <div class="max-h-48 overflow-y-auto space-y-2">
            <div
              v-for="func in fileDetail.uncovered_functions"
              :key="func"
              class="flex items-center border border-red-100 rounded bg-white p-2"
            >
              <function-outlined class="mr-2 text-red-500" />
              <code class="text-sm text-gray-800">{{ func }}</code>
            </div>
            <p v-if="uncoveredFunctionsCount === 0" class="text-sm text-green-600">🎉 所有函数都已被覆盖！</p>
          </div>
        </div>
      </div>

      <!-- 风险分析 -->
      <div v-if="hasRiskGaps" class="mb-6 border rounded-lg p-4" :class="getRiskClass()">
        <div class="mb-3 flex items-center">
          <warning-outlined :class="getRiskIconClass()" class="mr-2" />
          <h5 class="font-semibold" :class="getRiskTextClass()">风险分析 - {{ getHighestRiskLevel() }} 级别</h5>
        </div>
        <p class="mb-3 text-sm" :class="getRiskTextClass()">{{ getHighestRiskReason() }}</p>
        <div class="space-y-1">
          <p class="text-xs font-medium" :class="getRiskTextClass()">风险函数：</p>
          <div class="flex flex-wrap gap-1">
            <code
              v-for="func in getHighestRiskFunctions()"
              :key="func"
              class="mb-1 mr-2 inline-block rounded bg-white px-2 py-1 text-xs"
              :class="getRiskTextClass()"
            >
              {{ func }}
            </code>
          </div>
        </div>
      </div>

      <!-- 操作建议 -->
      <div class="border border-blue-200 rounded-lg bg-blue-50 p-4">
        <div class="mb-3 flex items-center">
          <bulb-outlined class="mr-2 text-blue-600" />
          <h5 class="font-semibold text-blue-800">改进建议</h5>
        </div>
        <ul class="text-sm text-blue-700 space-y-1">
          <li v-if="coveragePercentage < 80">考虑为未覆盖的函数增加测试用例</li>
          <li v-if="hasHighRiskGaps">高风险函数应优先增加测试覆盖</li>
          <li v-if="uncoveredFunctionsCount > 0">重点关注核心业务逻辑函数的测试覆盖</li>
          <li v-else>🎉 覆盖率良好，继续保持！</li>
          <li>定期进行回归测试确保覆盖率稳定</li>
        </ul>
      </div>
    </div>
  </a-modal>
</template>

<script setup lang="ts">
import type { CoverageGap, FileCoverageDetail } from '@/types/precision-new/ai-analysis';
import {
  BulbOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  FunctionOutlined,
  WarningOutlined,
} from '@ant-design/icons-vue';
import { computed } from 'vue';

// Props
interface Props {
  visible: boolean;
  fileDetail?: FileCoverageDetail;
  coverageGaps?: CoverageGap[];
}

const props = withDefaults(defineProps<Props>(), {
  visible: false,
  fileDetail: undefined,
  coverageGaps: () => [],
});

// Emits
const emit = defineEmits(['update:visible', 'close']);

// Computed
const modalTitle = computed(() => {
  if (!props.fileDetail) {
    return '文件覆盖详情';
  }
  const fileName = props.fileDetail.file_path.split('/').pop();
  return `${fileName} - 覆盖详情分析`;
});

const coveragePercentage = computed(() => {
  return Math.round((props.fileDetail?.estimated_coverage || 0) * 100);
});

const coveredFunctionsCount = computed(() => {
  return props.fileDetail?.covered_functions?.length || 0;
});

const uncoveredFunctionsCount = computed(() => {
  return props.fileDetail?.uncovered_functions?.length || 0;
});

const hasRiskGaps = computed(() => {
  return props.coverageGaps && props.coverageGaps.length > 0;
});

const hasHighRiskGaps = computed(() => {
  return props.coverageGaps?.some((gap) => gap.risk_level === 'high' || gap.risk_level === 'critical') || false;
});

const highestRiskGap = computed(() => {
  if (!props.coverageGaps || props.coverageGaps.length === 0) {
    return null;
  }

  const riskLevels = ['critical', 'high', 'medium', 'low'];
  for (const level of riskLevels) {
    const gap = props.coverageGaps.find((g) => g.risk_level === level);
    if (gap) {
      return gap;
    }
  }
  return props.coverageGaps[0];
});

// 样式方法
function getOverallCoverageClass() {
  const percentage = coveragePercentage.value;
  if (percentage >= 80) {
    return 'bg-green-50 border-green-200';
  }
  if (percentage >= 60) {
    return 'bg-yellow-50 border-yellow-200';
  }
  return 'bg-red-50 border-red-200';
}

function getCoverageTextClass() {
  const percentage = coveragePercentage.value;
  if (percentage >= 80) {
    return 'text-green-600';
  }
  if (percentage >= 60) {
    return 'text-yellow-600';
  }
  return 'text-red-500';
}

function getCoverageBarClass() {
  const percentage = coveragePercentage.value;
  if (percentage >= 80) {
    return 'bg-green-500';
  }
  if (percentage >= 60) {
    return 'bg-yellow-500';
  }
  return 'bg-red-500';
}

function getRiskClass() {
  if (!highestRiskGap.value) {
    return 'bg-gray-50 border-gray-200';
  }
  const riskClasses = {
    critical: 'bg-red-50 border-red-300',
    high: 'bg-orange-50 border-orange-300',
    medium: 'bg-yellow-50 border-yellow-300',
    low: 'bg-blue-50 border-blue-300',
  };
  return riskClasses[highestRiskGap.value.risk_level as keyof typeof riskClasses] || 'bg-gray-50 border-gray-200';
}

function getRiskIconClass() {
  if (!highestRiskGap.value) {
    return 'text-gray-500';
  }
  const riskClasses = {
    critical: 'text-red-600',
    high: 'text-orange-600',
    medium: 'text-yellow-600',
    low: 'text-blue-600',
  };
  return riskClasses[highestRiskGap.value.risk_level as keyof typeof riskClasses] || 'text-gray-500';
}

function getRiskTextClass() {
  if (!highestRiskGap.value) {
    return 'text-gray-600';
  }
  const riskClasses = {
    critical: 'text-red-800',
    high: 'text-orange-800',
    medium: 'text-yellow-800',
    low: 'text-blue-800',
  };
  return riskClasses[highestRiskGap.value.risk_level as keyof typeof riskClasses] || 'text-gray-600';
}

function getHighestRiskLevel() {
  if (!highestRiskGap.value) {
    return '';
  }
  const riskTexts = {
    critical: '严重',
    high: '高',
    medium: '中',
    low: '低',
  };
  return riskTexts[highestRiskGap.value.risk_level as keyof typeof riskTexts] || '';
}

function getHighestRiskReason() {
  return highestRiskGap.value?.reason || '';
}

function getHighestRiskFunctions() {
  return highestRiskGap.value?.functions || [];
}

function handleClose() {
  emit('update:visible', false);
  emit('close');
}
</script>

<style scoped>
.file-detail-modal :deep(.ant-modal-content) {
  max-height: 85vh;
}

.file-detail-modal :deep(.ant-modal-body) {
  max-height: 70vh;
  overflow-y: auto;
}

.file-detail-modal :deep(.ant-modal-body)::-webkit-scrollbar {
  width: 6px;
}

.file-detail-modal :deep(.ant-modal-body)::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}

.file-detail-modal :deep(.ant-modal-body)::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.file-detail-modal :deep(.ant-modal-body)::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
</style>
